import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LayoutGrid, Image, Table, Check, Sparkles } from 'lucide-react';
import { apiService } from '@/lib/api';
import { toast } from 'sonner';

const layouts = [
  {
    id: 'control_tower',
    name: 'Control Tower',
    description: "Bird's-eye view with key metrics and quick actions",
    icon: LayoutGrid,
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-500',
    textColor: 'text-blue-700',
  },
  {
    id: 'visual_site',
    name: 'Visual Site',
    description: 'Project cards with progress visuals and status indicators',
    icon: Image,
    color: 'from-emerald-500 to-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-500',
    textColor: 'text-emerald-700',
  },
  {
    id: 'financial_ledger',
    name: 'Financial Ledger',
    description: 'Detailed financial breakdown with draw tracking',
    icon: Table,
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-500',
    textColor: 'text-purple-700',
  },
];

export default function FeatureAnnouncementModal({ isOpen, userId, onLayoutSelected }) {
  const navigate = useNavigate();
  const [selectedLayout, setSelectedLayout] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSelectLayout = async () => {
    if (!selectedLayout) return;
    
    setIsSubmitting(true);
    try {
      // Update preference and mark feature as seen
      await apiService.updateDashboardPreference(userId, selectedLayout, true);
      toast.success('Dashboard layout saved!');
      
      // Notify parent and navigate
      if (onLayoutSelected) {
        onLayoutSelected(selectedLayout);
      }
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to save layout preference:', error);
      toast.error('Failed to save preference. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} modal={true}>
      <DialogContent 
        className="sm:max-w-[600px] p-0 overflow-hidden [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2" data-testid="feature-modal-title">
            Choose Your Dashboard Layout
          </h2>
          <p className="text-blue-100 text-sm">
            We've redesigned your dashboard! Pick the view that works best for you.
          </p>
        </div>

        {/* Layout Options */}
        <div className="p-6 space-y-3">
          {layouts.map((layout) => {
            const Icon = layout.icon;
            const isSelected = selectedLayout === layout.id;
            
            return (
              <button
                key={layout.id}
                onClick={() => setSelectedLayout(layout.id)}
                data-testid={`layout-option-${layout.id}`}
                className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 text-left
                  ${isSelected 
                    ? `${layout.borderColor} ${layout.bgColor} shadow-md` 
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
              >
                <div className={`flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br ${layout.color} flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className={`font-semibold ${isSelected ? layout.textColor : 'text-slate-900'}`}>
                      {layout.name}
                    </h3>
                    {isSelected && (
                      <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br ${layout.color}`}>
                        <Check className="w-3 h-3 text-white" />
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">{layout.description}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <Button
            onClick={handleSelectLayout}
            disabled={!selectedLayout || isSubmitting}
            data-testid="confirm-layout-btn"
            className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : selectedLayout ? 'Continue with This Layout' : 'Select a Layout'}
          </Button>
          <p className="text-center text-xs text-slate-400 mt-3">
            You can change this anytime via the "Layout" button on your dashboard.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
